'use client'
import { useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'

import {
  COMPANY_STATUS,
  DISCONNECTED_UNKNOWN_NUMBER_TOAST_ID,
  ROLES,
} from '@/shared/constants'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { RootState } from '@/store'
import {
  setIsOfficialApiActivationOpen,
  setIsConnected,
  setIsConnectToWhatsappDialogOpen,
  setIsFallbackConnected,
  setIsUpdateWhatsAppNumberDialogOpen,
} from '@/store/features/whatsappSlice'
import useWhatsappApi from '@/hooks/useWhatsAppApi'
import ConnectToWhatsappDialog from '@/components/global/dialogs/ConnectToWhatsappDialog'
import OfficialApiActivationDialog from '../whatsappCosts/OfficialApiActivationDialog'
import UpdateWhatsappNumberDialog from './UpdateWhatsappNumberDialog'
import { disconnectSocket, getSocket } from '@/socket'

const WHATSAPP_INTEGRATION_NUMBER_NOT_FOUND = 92

export default function WhatsappStatusControl() {
  const { getStatusDetails } = useWhatsappApi()
  const { isOfficialApiActivationOpen, isConnectToWhatsappDialogOpen, isUpdateWhatsAppNumberDialogOpen } =
    useAppSelector((state: RootState) => state.whatsapp)
  const { user } = useAppSelector((state: RootState) => state.auth)
  const dispatch = useAppDispatch()

  const shouldHandleWhatsappStatus = useMemo(
    () =>
      user?.role.id &&
      user.role.id !== ROLES.ADMIN &&
      user.company?.status !== COMPANY_STATUS.RESTRICTED_ACCESS &&
      user.company?.status !== COMPANY_STATUS.CANCELED,
    [user]
  )

  useEffect(() => {
    if (!user || !user.companyId || user.role.id === ROLES.ADMIN) return

    const socket = getSocket(user.companyId, user.id)

    if (user && shouldHandleWhatsappStatus) {
      socket.on(
        `disconnected-number-${user.company?.whatsappIntegration?.id}`,
        () => {
          dispatch(setIsConnected(false))
          dispatch(setIsConnectToWhatsappDialogOpen(true))
        }
      )
      socket.on(
        `disconnected-unknown-number-${user.company?.whatsappIntegration?.id}`,
        () => {
          toast.error(
            'Conecte o mesmo número salvo nas configurações da empresa',
            {
              autoClose: false,
              closeOnClick: true,
              toastId: DISCONNECTED_UNKNOWN_NUMBER_TOAST_ID,
            }
          )
          dispatch(setIsConnected(false))
          dispatch(setIsConnectToWhatsappDialogOpen(true))
        }
      )

      return () => {
        socket.off(
          `disconnected-number-${user.company?.whatsappIntegration?.id}`
        )
        socket.off(
          `disconnected-unknown-number-${user.company?.whatsappIntegration?.id}`
        )
        disconnectSocket()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (shouldHandleWhatsappStatus) {
      handleGetStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const handleGetStatus = async () => {
    const status = await getStatusDetails()

    if (typeof status !== 'number') {
      dispatch(setIsConnected(status.connected))
      dispatch(setIsFallbackConnected(null))

      if (!status.connected) {
        dispatch(setIsConnectToWhatsappDialogOpen(true))
      } else {
        toast.dismiss(DISCONNECTED_UNKNOWN_NUMBER_TOAST_ID)
      }
    } else if (WHATSAPP_INTEGRATION_NUMBER_NOT_FOUND === status) {
      dispatch(setIsUpdateWhatsAppNumberDialogOpen(true))
      dispatch(setIsConnected(null))
      dispatch(setIsFallbackConnected(null))
    }
  }

  return (
    <>
      {shouldHandleWhatsappStatus && isOfficialApiActivationOpen && <OfficialApiActivationDialog open onClose={() => dispatch(setIsOfficialApiActivationOpen(false))} />}
      {shouldHandleWhatsappStatus && isConnectToWhatsappDialogOpen && (
        <ConnectToWhatsappDialog
          open={isConnectToWhatsappDialogOpen}
          onClose={() => dispatch(setIsConnectToWhatsappDialogOpen(false))}
        />
      )}
      {shouldHandleWhatsappStatus && isUpdateWhatsAppNumberDialogOpen && (
        <UpdateWhatsappNumberDialog
          onClose={() => dispatch(setIsUpdateWhatsAppNumberDialogOpen(false))}
          open={isUpdateWhatsAppNumberDialogOpen}
        />
      )}
    </>
  )
}
